import type { ReactNode } from "react";

/* Minimal Markdown renderer for assistant replies.
 *
 * DEVIATION FROM LEGACY (approved): legacy printed the model's Markdown as
 * plain text, so "**bold**" and "# heading" showed their punctuation. The
 * model reliably emits Markdown, so we render a deliberately small subset.
 *
 * SAFETY: this parser emits React nodes. It never builds an HTML string and
 * never touches dangerouslySetInnerHTML, so anything in the model's output
 * that looks like markup -- "<script>", "<img onerror=...>", "javascript:" --
 * becomes a text node and is displayed, not executed. There is no sanitizer
 * to bypass because there is no HTML parse step. Do not "optimise" this into
 * an innerHTML implementation.
 *
 * SUPPORTED, and nothing else: **bold**, # headings (h1-h6), - and 1. lists,
 * paragraphs, and line breaks. Links, images, tables, code fences, raw HTML
 * and italics are intentionally NOT rendered -- they pass through as the
 * literal characters the model wrote.
 */

/* ---- inline: **bold** only ---- */

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  // Non-greedy so "**a** and **b**" is two spans, not one. Requires a
  // non-empty body, so "****" stays literal.
  const BOLD = /\*\*(.+?)\*\*/;
  for (;;) {
    const m = BOLD.exec(rest);
    if (!m || m.index === undefined) break;
    if (m.index > 0) out.push(rest.slice(0, m.index));
    out.push(<strong key={`${keyBase}-b${i++}`}>{m[1]}</strong>);
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest) out.push(rest);
  return out.length ? out : [text];
}

/* Single newlines inside a paragraph are breaks, not new paragraphs. */
function withBreaks(lines: string[], keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push(<br key={`${keyBase}-br${i}`} />);
    out.push(...inline(line, `${keyBase}-l${i}`));
  });
  return out;
}

/* ---- blocks ---- */

const HEADING = /^(#{1,6})\s+(.*)$/;
// U+2022 is not Markdown, but the model emits it for Arabic lists in
// practice, so it counts as a bullet marker here.
const BULLET = /^\s*[-*+•]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;

export function renderMarkdown(source: string): ReactNode[] {
  const lines = String(source ?? "").replace(/\r\n?/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let key = 0;

  function flushPara() {
    if (!para.length) return;
    blocks.push(<p key={`p${key++}`}>{withBreaks(para, `p${key}`)}</p>);
    para = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (!line.trim()) { flushPara(); continue; }

    const h = HEADING.exec(line);
    if (h) {
      flushPara();
      const level = Math.min(6, h[1].length);
      // A fixed map, not a computed tag name, so the element set is closed.
      const body = inline(h[2], `h${key}`);
      const k = `h${key++}`;
      blocks.push(
        level === 1 ? <h3 key={k} className="sq-md__h sq-md__h--1">{body}</h3>
        : level === 2 ? <h4 key={k} className="sq-md__h sq-md__h--2">{body}</h4>
        : <h5 key={k} className="sq-md__h sq-md__h--3">{body}</h5>,
      );
      continue;
    }

    const isBullet = BULLET.test(line);
    const isNumber = !isBullet && NUMBER.test(line);
    if (isBullet || isNumber) {
      flushPara();
      const items: string[] = [];
      const re = isBullet ? BULLET : NUMBER;
      while (i < lines.length) {
        const m = re.exec(lines[i]);
        if (!m) break;
        items.push(m[1]);
        i++;
      }
      i--; // the loop's i++ re-reads the terminating line
      const k = `l${key++}`;
      const li = items.map((it, n) => <li key={`${k}-${n}`}>{inline(it, `${k}-${n}`)}</li>);
      blocks.push(isBullet
        ? <ul key={k} className="sq-md__list">{li}</ul>
        : <ol key={k} className="sq-md__list">{li}</ol>);
      continue;
    }

    para.push(line);
  }
  flushPara();
  return blocks;
}

/* The only component that should render assistant text. */
export function Markdown({ text }: { text: string }) {
  return <div className="sq-md">{renderMarkdown(text)}</div>;
}
